import React, { useState } from "react";
import { apiClient } from "../../../api";
import { toast } from "react-toastify";
import { Loader2, QrCode, CheckCircle2, AlertCircle } from "lucide-react";

export default function LinkQRStep({ propertyId, onComplete, onShopQR, onSuccessNavigate }) {
    const [codeArray, setCodeArray] = useState(new Array(7).fill(""));
    const [isLinking, setIsLinking] = useState(false);
    const [hasLinkedAnyQR, setHasLinkedAnyQR] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const shortCode = codeArray.join("").trim();

    const handleChange = (e, index) => {
        const val = e.target.value.toUpperCase();
        // Allow only alphanumeric characters (A-Z, 0-9)
        if (val && !/^[A-Z0-9]$/.test(val)) return;

        const newArray = [...codeArray];
        newArray[index] = val;
        setCodeArray(newArray);

        // Auto-focus the next input field
        if (val && index < 6) {
            const nextInput = document.getElementById(`code-input-${index + 1}`);
            if (nextInput) {
                nextInput.focus();
                // Select text for easier rewriting
                setTimeout(() => nextInput.select(), 10);
            }
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            const newArray = [...codeArray];
            
            if (codeArray[index]) {
                // If current slot has a character, just delete it
                newArray[index] = "";
                setCodeArray(newArray);
            } else if (index > 0) {
                // If current slot is empty, move back and delete previous character
                newArray[index - 1] = "";
                setCodeArray(newArray);
                const prevInput = document.getElementById(`code-input-${index - 1}`);
                if (prevInput) {
                    prevInput.focus();
                }
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            document.getElementById(`code-input-${index - 1}`)?.focus();
        } else if (e.key === "ArrowRight" && index < 6) {
            document.getElementById(`code-input-${index + 1}`)?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        // Get pasted text and normalize it
        const pastedData = e.clipboardData
            .getData("text")
            .toUpperCase()
            .trim()
            .replace(/[^A-Z0-9]/g, "");

        if (pastedData.length > 0) {
            const newArray = [...codeArray];
            // Fill up to 7 characters from the pasted string
            const fillLength = Math.min(pastedData.length, 7);
            for (let i = 0; i < fillLength; i++) {
                newArray[i] = pastedData[i];
            }
            setCodeArray(newArray);

            // Focus the appropriate input after paste
            const focusIndex = Math.min(fillLength, 6);
            const targetInput = document.getElementById(`code-input-${focusIndex}`);
            if (targetInput) {
                targetInput.focus();
            }
        }
    };

    const handleLinkQR = async (e) => {
        if (e) e.preventDefault();
        
        if (shortCode.length !== 7) {
            toast.error("Please enter the complete 7-character shortcode.");
            return;
        }

        setIsLinking(true);
        try {
            const response = await apiClient.post("/api/qrs/claim", {
                shortCode: shortCode,
                propertyId: propertyId,
            });

            if (response.status === 200) {
                const serverMsg = response.data.message || "QR Code linked successfully!";
                toast.success(serverMsg);
                if (onSuccessNavigate) {
                    onSuccessNavigate();
                } else {
                    setSuccessMessage(serverMsg);
                    setHasLinkedAnyQR(true);
                    setCodeArray(new Array(7).fill("")); // Reset code inputs
                }
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || "Failed to link QR code.";
            toast.error(errorMsg);
        } finally {
            setIsLinking(false);
        }
    };

    return (
        <form onSubmit={handleLinkQR} className="mx-auto bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-8 sm:py-4 gap-4 sm:gap-0">
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">Link a QR Stand</h2>
                    <p className="mt-2 text-sm md:text-base text-gray-600">
                        To pair this property with a physical QR stand, enter the unique shortcode printed on it.
                    </p>
                </div>
                {!successMessage && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto justify-end">
                        <a
                            href="http://review.onelyk.com/customer/new"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 px-4 py-2 w-full sm:w-auto text-center"
                        >
                            Help
                        </a>
                        <button
                            type="button"
                            onClick={onComplete}
                            className="w-full sm:w-auto flex-1 sm:flex-none rounded-md bg-gray-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                        >
                            Skip
                        </button>
                        <button
                            type="submit"
                            disabled={isLinking || shortCode.length !== 7}
                            className="w-full sm:w-auto flex-1 sm:flex-none rounded-md bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLinking ? (
                                <div className="flex items-center justify-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Linking...</span>
                                </div>
                            ) : (
                                <span>Link QR Code</span>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Body Section */}
            <div className="px-4 py-4 sm:py-6 sm:p-8">
                {successMessage ? (
                    <div className="text-center py-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">Successfully Linked!</h3>
                        <p className="text-sm text-gray-600 mb-6">Your QR stand has been paired with this property.</p>
                        
                        <div className="flex gap-3 max-w-sm mx-auto">
                            <button
                                type="button"
                                onClick={() => setSuccessMessage("")}
                                className="flex-1 rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                Add Another Code
                            </button>
                            <button
                                type="button"
                                onClick={onComplete}
                                className="flex-1 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            >
                                Complete Setup
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-8 max-w-sm mx-auto mt-4 mb-4">
                        <div className="space-y-4">
                            <label className="block text-sm font-medium mb-2 text-gray-700 text-center">
                                Enter 7-Char Shortcode
                            </label>
                            
                            {/* 7-Character input slots */}
                            <div className="flex justify-between gap-1.5 sm:gap-2.5" onPaste={handlePaste}>
                                {codeArray.map((char, index) => (
                                    <input
                                        key={index}
                                        id={`code-input-${index}`}
                                        type="text"
                                        value={char}
                                        onChange={(e) => handleChange(e, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        maxLength={1}
                                        autoFocus={index === 0}
                                        className="w-10 h-12 sm:w-12 sm:h-14 rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-lg sm:text-xl font-bold text-center uppercase transition-all outline-none"
                                    />
                                ))}
                            </div>

                            <div className="flex items-start gap-2 text-gray-500 mt-2">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span className="text-xs font-medium leading-relaxed">
                                    Enter the shortcode printed on the front of the stand (e.g. ABC1234).
                                </span>
                            </div>
                        </div>

                        <div className="mt-4 text-center">
                            <button
                                type="button"
                                onClick={onShopQR}
                                className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 transition-colors"
                            >
                                I don't have a QR stand
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </form>
    );
}
